import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MerchantApplicationService } from '../services/merchantApplicationService';
import { MerchantApplication, PipelineStatus } from '../types';
import { PIPELINE_CONFIG } from '../config/constants';
import { getDisplayName, getEmailAddress, getPhoneNumber, getProcessingVolume, getPipelineCardTitle, getCustomerFullName } from '../utils/dataHelpers';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Clock,
  Building2,
  ExternalLink,
  MoreVertical,
  Edit,
  Trash,
  Plus,
  Download,
  ArrowRight,
  X,
  User,
  Mail,
  Eye,
  AlertTriangle
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { formatDistanceToNow } from 'date-fns';
import { useToast } from '@/components/ui/use-toast';
import { doc, updateDoc, addDoc, deleteDoc, collection, getDocs, writeBatch, setDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  DndContext,
  closestCorners,
  pointerWithin,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverEvent,
  UniqueIdentifier,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  horizontalListSortingStrategy,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';

interface PipelineColumn {
  id: PipelineStatus | string;
  title: string;
  color: string;
  applications: MerchantApplication[];
  position: number;
  emailConfig?: {
    templateId: string | null;
    autoSend: boolean;
    enabled: boolean;
  };
}

const DEFAULT_COLORS = Object.values(PIPELINE_CONFIG.COLUMN_COLORS);

// Sortable Application Component
interface SortableApplicationProps {
  application: MerchantApplication;
  column: PipelineColumn;
  bulkActionMode: boolean;
  isSelected: boolean;
  onToggleSelection: (applicationId: string) => void;
  onNavigate: (applicationId: string) => void;
}

function SortableApplication({
  application,
  column,
  bulkActionMode,
  isSelected,
  onToggleSelection,
  onNavigate,
}: SortableApplicationProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: application.id,
    data: {
      type: 'application',
      application,
      fromColumn: column.id,
    },
    disabled: bulkActionMode,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const data = application.data;
  const cardTitle = getPipelineCardTitle(data);
  const customerName = getCustomerFullName(data);

  return (
    <Card
      ref={setNodeRef}
      style={style}
      {...(!bulkActionMode ? attributes : {})}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('button')) return;
        if ((e.target as HTMLElement).closest('[role="checkbox"]')) return;
        if ((e.target as HTMLElement).closest('[data-drag-handle]')) return;
        if (bulkActionMode) {
          onToggleSelection(application.id);
        } else {
          onNavigate(application.id);
        }
      }}
      className={cn(
        "cursor-pointer border-gray-200 hover:border-gray-300 hover:shadow-md transition-all duration-200 bg-white",
        bulkActionMode && isSelected && "ring-2 ring-blue-500 border-blue-500",
        isDragging && "opacity-50 ring-2 ring-blue-400"
      )}
    >
      <CardContent className="p-3">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            {bulkActionMode ? (
              <Checkbox
                checked={isSelected}
                onCheckedChange={() => onToggleSelection(application.id)}
                className="mr-2 mt-0.5 h-3 w-3"
              />
            ) : (
              <div
                data-drag-handle
                {...listeners}
                className="cursor-grab active:cursor-grabbing p-1 -ml-1 mr-1 hover:bg-gray-100 rounded"
              >
                <GripVertical className="h-3 w-3 text-gray-400" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-gray-400 flex-shrink-0" />
                <h4
                  className="font-medium text-sm truncate"
                  style={{ color: column.color }}
                >
                  {cardTitle}
                </h4>
              </div>
            </div>
            {!bulkActionMode && (
              <Button
                size="sm"
                variant="ghost"
                className="h-6 w-6 p-0 hover:bg-gray-100"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(`/app/merchant-applications/${application.id}`, '_blank');
                }}
              >
                <ExternalLink className="h-3 w-3 text-gray-500" />
              </Button>
            )}
          </div>

          {customerName && (
            <div className="flex items-center gap-2 text-xs text-gray-600">
              <User className="h-3 w-3 text-gray-400 flex-shrink-0" />
              <span className="truncate font-medium">{customerName}</span>
            </div>
          )}

          <div className="flex items-center justify-between pt-2 text-xs text-gray-500">
            <div className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              <span>{formatDistanceToNow(application.createdAt instanceof Date ? application.createdAt : (application.createdAt as any).toDate?.() || new Date(), { addSuffix: true })}</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Sortable Column Component
interface SortableColumnProps {
  column: PipelineColumn;
  columnWidth: number;
  shouldScroll: boolean;
  isDragging: boolean;
  dragOverColumn: string | null;
  bulkActionMode: boolean;
  selectedApplications: Set<string>;
  onEditColumn: (column: PipelineColumn) => void;
  onDeleteColumn: (columnId: string) => void;
  onToggleSelection: (applicationId: string) => void;
  onNavigate: (applicationId: string) => void;
}

function SortableColumn({
  column,
  columnWidth,
  shouldScroll,
  isDragging,
  dragOverColumn,
  bulkActionMode,
  selectedApplications,
  onEditColumn,
  onDeleteColumn,
  onToggleSelection,
  onNavigate,
}: SortableColumnProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isColumnDragging,
  } = useSortable({
    id: column.id,
    data: {
      type: 'column',
      column,
    },
    disabled: bulkActionMode,
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isColumnDragging ? 0 : 1,
    width: `${columnWidth}px`,
    flexShrink: shouldScroll ? 0 : 1,
  };

  const applicationIds = column.applications.map(app => app.id);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="pipeline-column"
    >
      {/* Column Header with Drag Handle */}
      <div
        {...(!bulkActionMode && attributes)}
        {...(!bulkActionMode && listeners)}
        className="rounded-t-xl border-b-0 p-4 flex items-center justify-between cursor-grab active:cursor-grabbing user-select-none"
        style={{
          backgroundColor: column.color,
          touchAction: bulkActionMode ? 'auto' : 'none',
          userSelect: 'none'
        }}
      >
        {/* Left side content */}
        <div className="flex items-center gap-3 select-none flex-1">
          {/* Drag Handle - Only visible when not in bulk action mode */}
          {!bulkActionMode && (
            <div
              className="p-1 text-white/70 hover:text-white transition-colors flex-shrink-0"
              title="Drag to reorder column"
            >
              <GripVertical className="h-4 w-4" />
            </div>
          )}

          <h3 className="font-bold text-white text-sm tracking-wide uppercase">
            {column.title}
          </h3>
          <div className="bg-white/20 backdrop-blur-sm rounded-full px-2 py-1 min-w-[24px] flex items-center justify-center">
            <span className="text-white font-bold text-xs">
              {column.applications.length}
            </span>
          </div>
        </div>

        {/* Right side - Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0 hover:bg-white/20 text-white flex-shrink-0">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem onClick={() => onEditColumn(column)}>
              <Edit className="h-4 w-4 mr-2" />
              Edit Stage
            </DropdownMenuItem>
            {!['lead', 'phone', 'offer', 'underwriting', 'documents', 'approved', 'onboarded', 'declined'].includes(column.id) && (
              <DropdownMenuItem
                onClick={() => onDeleteColumn(column.id)}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <Trash className="h-4 w-4 mr-2" />
                Delete Stage
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Column Content - Applications */}
      <div
        className={cn(
          "bg-gray-50 rounded-b-xl border-x border-b border-gray-200 p-3 space-y-3 overflow-y-auto transition-all duration-200",
          isDragging && dragOverColumn === column.id && "bg-blue-50 border-blue-300 ring-2 ring-blue-200"
        )}
        style={{ maxHeight: 'calc(100vh - 350px)', minHeight: '400px' }}
      >
        <SortableContext items={applicationIds} strategy={verticalListSortingStrategy}>
          {column.applications.length === 0 ? (
            <div className={cn(
              "text-center py-8 text-gray-400 transition-all duration-200",
              isDragging && dragOverColumn === column.id && "scale-105"
            )}>
              <div className="text-xs">No applications</div>
              <div className="text-xs mt-1">
                {isDragging && dragOverColumn === column.id ? (
                  <span className="text-blue-600 font-medium">Drop here</span>
                ) : (
                  'Drag applications here'
                )}
              </div>
            </div>
          ) : (
            column.applications.map((application) => (
              <SortableApplication
                key={application.id}
                application={application}
                column={column}
                bulkActionMode={bulkActionMode}
                isSelected={selectedApplications.has(application.id)}
                onToggleSelection={onToggleSelection}
                onNavigate={onNavigate}
              />
            ))
          )}
        </SortableContext>
      </div>
    </div>
  );
}

interface EnhancedMerchantPipelineProps {
  hideHeader?: boolean;
  onPipelineDataChange?: (data: {
    bulkActionMode: boolean;
    setBulkActionMode: (mode: boolean) => void;
    selectedApplicationsCount: number;
    applicationsCount: number;
    showAddColumn: boolean;
    setShowAddColumn: (show: boolean) => void;
    handleExportSelected: () => void;
    selectedApplications: Set<string>;
    setSelectedApplications: (apps: Set<string>) => void;
  }) => void;
}

export function EnhancedMerchantPipeline({ hideHeader = false, onPipelineDataChange }: EnhancedMerchantPipelineProps = {}) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [draggedApplication, setDraggedApplication] = useState<MerchantApplication | null>(null);
  const [editingColumn, setEditingColumn] = useState<string | null>(null);
  const [columnTitle, setColumnTitle] = useState('');
  const [columnColor, setColumnColor] = useState('');
  const [columnEmailTemplateId, setColumnEmailTemplateId] = useState<string>('');
  const [columnEmailAutoSend, setColumnEmailAutoSend] = useState(false);
  const [columnEmailEnabled, setColumnEmailEnabled] = useState(false);
  const [showAddColumn, setShowAddColumn] = useState(false);
  const [newColumnTitle, setNewColumnTitle] = useState('');
  const [newColumnColor, setNewColumnColor] = useState(DEFAULT_COLORS[0]);
  const [selectedApplications, setSelectedApplications] = useState<Set<string>>(new Set());
  const [bulkActionMode, setBulkActionMode] = useState(false);
  const [showBulkMoveDialog, setShowBulkMoveDialog] = useState(false);
  const [bulkMoveTarget, setBulkMoveTarget] = useState<string>('');
  const [isDragging, setIsDragging] = useState(false);
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [activeDragType, setActiveDragType] = useState<'column' | 'application' | null>(null);

  // Configure @dnd-kit sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px movement before drag starts
        delay: 100, // Add small delay to ensure drag intent is clear
      },
    })
  );
  const [emailPreviewModal, setEmailPreviewModal] = useState<{
    show: boolean;
    applicationId: string | null;
    targetStatus: string | null;
    template: { subject: string; content: string } | null;
  }>({ show: false, applicationId: null, targetStatus: null, template: null });

  // Track container width for responsive columns
  useEffect(() => {
    const container = document.getElementById('pipeline-container');
    if (!container) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });

    resizeObserver.observe(container);
    
    // Set initial width
    setContainerWidth(container.offsetWidth);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Fetch all applications
  const { data: applications = [], isLoading } = useQuery({
    queryKey: ['merchantApplications', 'all'],
    queryFn: () => MerchantApplicationService.getAllApplications(),
  });

  // Fetch pipeline columns configuration
  const { data: columnConfig = [] } = useQuery({
    queryKey: ['pipelineColumns'],
    queryFn: () => MerchantApplicationService.getPipelineColumns(),
  });

  // Fetch merchant email templates from global collection
  const { data: merchantEmailTemplates = [], isLoading: isLoadingTemplates, error: templatesError } = useQuery({
    queryKey: ['merchant-email-templates'],
    queryFn: async () => {
      try {
        const templatesRef = collection(db, 'merchantEmailTemplates');
        const snapshot = await getDocs(templatesRef);
        const templates = snapshot.docs.map(doc => ({
          id: doc.id,
          type: doc.data().type,
          subject: doc.data().subject,
          content: doc.data().content,
          enabled: doc.data().enabled,
          ...doc.data()
        }));
        console.log('[Global] Loaded merchant email templates:', templates);
        return templates;
      } catch (error) {
        console.error('Error loading merchant email templates:', error);
        throw error;
      }
    },
  });

  // Templates are loaded via useQuery above
  // Debugging: uncomment to log template state (may cause re-renders)
  // React.useEffect(() => {
  //   console.log('Merchant email templates:', merchantEmailTemplates.length, 'loaded');
  // }, [merchantEmailTemplates.length]);


  // Update pipeline status mutation
  const updateStatusMutation = useMutation({
    mutationFn: ({ applicationId, newStatus }: { applicationId: string; newStatus: PipelineStatus | string }) =>
      MerchantApplicationService.updatePipelineStatus(applicationId, newStatus as PipelineStatus),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['merchantApplications'] });
      toast({
        title: 'Application moved',
        description: 'Pipeline status updated successfully',
      });
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to update pipeline status',
        variant: 'destructive',
      });
    },
  });

  // Bulk move mutation
  const bulkMoveMutation = useMutation({
    mutationFn: async ({ applicationIds, newStatus }: { applicationIds: string[]; newStatus: PipelineStatus | string }) => {
      const promises = applicationIds.map(id => 
        MerchantApplicationService.updatePipelineStatus(id, newStatus as PipelineStatus)
      );
      await Promise.all(promises);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['merchantApplications'] });
      toast({
        title: 'Applications moved',
        description: `${selectedApplications.size} applications moved successfully`,
      });
      setSelectedApplications(new Set());
      setBulkActionMode(false);
      setShowBulkMoveDialog(false);
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to move applications',
        variant: 'destructive',
      });
    },
  });

  // Update column mutation
  const updateColumnMutation = useMutation({
    mutationFn: async ({ columnId, title, color, emailConfig }: {
      columnId: string;
      title: string;
      color: string;
      emailConfig?: {
        templateId: string | null;
        autoSend: boolean;
        enabled: boolean;
      }
    }) => {
      const docRef = doc(db, 'pipelineColumns', columnId);
      const updateData: any = { title, color, updatedAt: new Date() };
      if (emailConfig !== undefined) {
        updateData.emailConfig = emailConfig;
      }
      console.log('[UpdateColumnMutation] Updating pipeline column:', {
        columnId,
        title,
        color,
        emailConfig,
        updateData
      });
      await updateDoc(docRef, updateData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pipelineColumns'] });
      setEditingColumn(null);
      toast({
        title: 'Column updated',
        description: 'Pipeline column updated successfully',
      });
    },
    onError: (error: any) => {
      console.error('Failed to update column:', error);
      const errorMessage = error?.message || 'Failed to update column';
      const isPermissionError = errorMessage.includes('permission-denied') || errorMessage.includes('PERMISSION_DENIED');

      toast({
        title: 'Error',
        description: isPermissionError
          ? 'You must be a super admin to update pipeline settings. Contact your administrator.'
          : errorMessage,
        variant: 'destructive',
      });
    },
  });

  // Add column mutation
  const addColumnMutation = useMutation({
    mutationFn: async ({ title, color }: { title: string; color: string }) => {
      const position = columnConfig.length;
      const docId = title.toLowerCase().replace(/\s+/g, '_');
      await setDoc(doc(db, 'pipelineColumns', docId), {
        id: docId,
        title,
        color,
        position,
        createdAt: new Date(),
        updatedAt: new Date(),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pipelineColumns'] });
      setShowAddColumn(false);
      setNewColumnTitle('');
      setNewColumnColor(DEFAULT_COLORS[0]);
      toast({
        title: 'Column added',
        description: 'New pipeline column added successfully',
      });
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to add column',
        variant: 'destructive',
      });
    },
  });

  // Delete column mutation
  const deleteColumnMutation = useMutation({
    mutationFn: async (columnId: string) => {
      // Check if column has applications (case-insensitive)
      const hasApplications = applications.some(app =>
        app.pipelineStatus?.toLowerCase() === columnId?.toLowerCase()
      );
      if (hasApplications) {
        throw new Error('Cannot delete column with applications');
      }
      await deleteDoc(doc(db, 'pipelineColumns', columnId));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pipelineColumns'] });
      toast({
        title: 'Column deleted',
        description: 'Pipeline column deleted successfully',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete column',
        variant: 'destructive',
      });
    },
  });

  // Group applications by pipeline status (case-insensitive matching)
  const columns: PipelineColumn[] = columnConfig.map((col: {
    id: string;
    title: string;
    color: string;
    position?: number;
    emailConfig?: {
      templateId: string | null;
      autoSend: boolean;
      enabled: boolean;
    }
  }) => ({
    id: col.id,
    title: col.title,
    color: col.color,
    position: col.position || 0,
    applications: applications.filter((app) =>
      app.pipelineStatus?.toLowerCase() === col.id?.toLowerCase()
    ),
    emailConfig: col.emailConfig,
  })).sort((a, b) => a.position - b.position);

  // Fixed column width
  const FIXED_COLUMN_WIDTH = 240;

  const columnWidth = FIXED_COLUMN_WIDTH;
  const shouldScroll = true; // Always allow scrolling with fixed width




  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id);
    const data = event.active.data.current;
    setActiveDragType(data?.type || null);

    if (data?.type === 'application') {
      setDraggedApplication(data.application);
      setIsDragging(true);
    }
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;

    if (!over) {
      setDragOverColumn(null);
      return;
    }

    const activeData = active.data.current;
    const overData = over.data.current;

    // Visual feedback for application dragging (ignore if dragging a column)
    if (activeData?.type === 'application') {
      if (overData?.type === 'column') {
        setDragOverColumn(overData.column.id);
      } else if (overData?.type === 'application') {
        // Find column containing the over application
        const overColumn = columns.find(col =>
          col.applications.some(app => app.id === over.id)
        );
        if (overColumn) {
          setDragOverColumn(overColumn.id);
        }
      }
    } else if (activeData?.type === 'column') {
      // When dragging a column, don't highlight unless over another column
      if (overData?.type === 'column') {
        setDragOverColumn(overData.column.id);
      } else {
        setDragOverColumn(null);
      }
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    setActiveId(null);
    setActiveDragType(null);
    setIsDragging(false);
    setDragOverColumn(null);
    setDraggedApplication(null);

    if (!over || active.id === over.id) return;

    const activeData = active.data.current;
    const overData = over.data.current;

    // COLUMN REORDERING
    // Handle both column-to-column drops and column-to-application drops
    // (user might drag column over application area)
    let isColumnReorder = false;
    let targetColumnId: string | null = null;

    if (activeData?.type === 'column') {
      if (overData?.type === 'column') {
        // Direct column-to-column drop
        isColumnReorder = true;
        targetColumnId = over.id as string;
      } else if (overData?.type === 'application') {
        // Column dragged over an application - find which column it belongs to
        isColumnReorder = true;
        const targetColumn = columns.find(col =>
          col.applications.some(app => app.id === over.id)
        );
        targetColumnId = targetColumn?.id || null;
      }
    }

    if (isColumnReorder && targetColumnId) {
      const oldIndex = columns.findIndex(col => col.id === active.id);
      const newIndex = columns.findIndex(col => col.id === targetColumnId);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newColumns = arrayMove(columns, oldIndex, newIndex);

        // Optimistically update the query cache with just the config (without applications)
        // This matches what getPipelineColumns returns from Firebase
        const optimisticColumnConfig = newColumns.map((col, index) => {
          // Extract only the fields that come from Firebase
          const { applications, ...colConfig } = col;
          return {
            ...colConfig,
            position: index,
          };
        });

        queryClient.setQueryData(['pipelineColumns'], optimisticColumnConfig);

        try {
          // First, query Firebase to get actual existing documents
          const columnsSnapshot = await getDocs(collection(db, 'pipelineColumns'));
          const existingColumnIds = new Set(columnsSnapshot.docs.map(doc => doc.id));

          // Batch update positions ONLY for documents that exist in Firebase
          const batch = writeBatch(db);
          let updateCount = 0;

          newColumns.forEach((col, index) => {
            // Only update if this document actually exists in Firebase
            if (existingColumnIds.has(col.id)) {
              const colRef = doc(db, 'pipelineColumns', col.id);
              batch.update(colRef, {
                position: index,
                updatedAt: new Date()
              });
              updateCount++;
            }
          });

          if (updateCount > 0) {
            await batch.commit();
            // After successful commit, keep the optimistic update
            toast({
              title: 'Column reordered',
              description: `Updated ${updateCount} column${updateCount > 1 ? 's' : ''} successfully`,
            });
          } else {
            // Revert optimistic update if nothing was updated
            queryClient.invalidateQueries({ queryKey: ['pipelineColumns'] });
            toast({
              title: 'No updates',
              description: 'No existing columns found to update',
              variant: 'destructive',
            });
          }
        } catch (error) {
          console.error('Error updating column positions:', error);
          // Revert optimistic update on error
          queryClient.invalidateQueries({ queryKey: ['pipelineColumns'] });
          toast({
            title: 'Error',
            description: 'Failed to update column order',
            variant: 'destructive',
          });
        }
      }
      return;
    }

    // APPLICATION MOVEMENT BETWEEN COLUMNS
    if (activeData?.type === 'application') {
      const application = activeData.application;
      let targetColumnId: string | null = null;

      if (overData?.type === 'column') {
        targetColumnId = overData.column.id;
      } else if (overData?.type === 'application') {
        const overColumn = columns.find(col =>
          col.applications.some(app => app.id === over.id)
        );
        targetColumnId = overColumn?.id || null;
      }

      if (targetColumnId && application.pipelineStatus !== targetColumnId) {
        // Store previous status for rollback on error
        const previousStatus = application.pipelineStatus;

        // Optimistic update: immediately update the cache
        queryClient.setQueryData(['merchantApplications', 'all'], (oldData: MerchantApplication[] | undefined) => {
          if (!oldData) return oldData;
          return oldData.map(app =>
            app.id === application.id
              ? { ...app, pipelineStatus: targetColumnId }
              : app
          );
        });

        // Update the status in database
        updateStatusMutation.mutate({
          applicationId: application.id,
          newStatus: targetColumnId,
        }, {
          onError: () => {
            // Rollback optimistic update on error
            queryClient.setQueryData(['merchantApplications', 'all'], (oldData: MerchantApplication[] | undefined) => {
              if (!oldData) return oldData;
              return oldData.map(app =>
                app.id === application.id
                  ? { ...app, pipelineStatus: previousStatus }
                  : app
              );
            });
          }
        });

        // Check if target column has email notifications enabled
        const targetColumn = columns.find(col => col.id === targetColumnId);
        if (targetColumn?.emailConfig?.enabled && targetColumn.emailConfig.templateId) {
          // Find the email template
          const template = merchantEmailTemplates.find(
            (t: any) => t.type === targetColumn.emailConfig?.templateId
          );

          if (template) {
            // Auto-send is now handled by the server-side listener (merchantApplicationListener.js)
            // Only show preview modal for manual editing when autoSend is disabled
            if (!targetColumn.emailConfig.autoSend) {
              setEmailPreviewModal({
                show: true,
                applicationId: application.id,
                targetStatus: targetColumnId,
                template: {
                  subject: template.subject,
                  content: template.content
                }
              });
            }
          }
        }
      }
    }
  };

  const formatApplicationDate = (timestamp: Date | { toDate: () => Date } | string | null) => {
    if (!timestamp) return 'Unknown';
    const date = typeof timestamp === 'object' && 'toDate' in timestamp 
      ? timestamp.toDate() 
      : new Date(timestamp as string | Date);
    return formatDistanceToNow(date, { addSuffix: true });
  };

  const handleEditColumn = (column: PipelineColumn) => {
    // Ensure column ID is normalized to lowercase
    const normalizedId = column.id.toLowerCase().replace(/\s+/g, '_');
    setEditingColumn(normalizedId);
    setColumnTitle(column.title);
    setColumnColor(column.color);
    setColumnEmailTemplateId(column.emailConfig?.templateId || '');
    setColumnEmailAutoSend(column.emailConfig?.autoSend || false);
    setColumnEmailEnabled(column.emailConfig?.enabled || false);
  };

  const handleUpdateColumn = () => {
    if (editingColumn && columnTitle && columnColor) {
      updateColumnMutation.mutate({
        columnId: editingColumn,
        title: columnTitle,
        color: columnColor,
        emailConfig: {
          templateId: columnEmailTemplateId || null,
          autoSend: columnEmailAutoSend,
          enabled: columnEmailEnabled
        }
      });
    }
  };

  const handleDeleteColumn = (columnId: string) => {
    if (confirm('Are you sure you want to delete this column?')) {
      deleteColumnMutation.mutate(columnId);
    }
  };

  const handleAddColumn = () => {
    if (newColumnTitle) {
      addColumnMutation.mutate({
        title: newColumnTitle,
        color: newColumnColor,
      });
    }
  };

  const toggleApplicationSelection = (applicationId: string) => {
    const newSelection = new Set(selectedApplications);
    if (newSelection.has(applicationId)) {
      newSelection.delete(applicationId);
    } else {
      newSelection.add(applicationId);
    }
    setSelectedApplications(newSelection);
  };

  const handleBulkMove = () => {
    if (bulkMoveTarget && selectedApplications.size > 0) {
      bulkMoveMutation.mutate({
        applicationIds: Array.from(selectedApplications),
        newStatus: bulkMoveTarget,
      });
    }
  };

  const handleExportSelected = useCallback(() => {
    const selectedApps = applications.filter(app => selectedApplications.has(app.id));
    const csvData = selectedApps.map(app => {
      // Handle both nested and flattened data structures
      const data = app.data;
      const businessName = getDisplayName(data);
      const email = getEmailAddress(data) || '';
      const phone = getPhoneNumber(data) || '';
      const businessType = data?.corporationInfo?.corporationType || '';
      const monthlyVolume = getProcessingVolume(data);
      
      // Handle date conversion
      let createdDate = '';
      if (app.createdAt) {
        const date = typeof app.createdAt === 'object' && 'toDate' in app.createdAt
          ? app.createdAt.toDate()
          : new Date(app.createdAt as string | Date);
        createdDate = date.toLocaleDateString();
      }
      
      return {
        'Business Name': businessName,
        'Email': email,
        'Phone': phone,
        'Status': app.status,
        'Pipeline Stage': app.pipelineStatus,
        'Created Date': createdDate,
        'Monthly Volume': monthlyVolume,
        'Business Type': businessType,
      };
    });

    const headers = Object.keys(csvData[0] || {});
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => headers.map(header => `"${row[header] || ''}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `merchant-applications-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast({
      title: 'Export successful',
      description: `Exported ${selectedApplications.size} applications`,
    });
  }, [applications, selectedApplications, toast]);

  // Create stable callback functions using useCallback
  const stableBulkActionModeSetter = useCallback((mode: boolean) => setBulkActionMode(mode), []);
  const stableShowAddColumnSetter = useCallback((show: boolean) => setShowAddColumn(show), []);
  const stableSelectedApplicationsSetter = useCallback((apps: Set<string>) => setSelectedApplications(apps), []);

  // Create memoized pipeline data with stable references
  const pipelineData = React.useMemo(() => ({
    bulkActionMode,
    setBulkActionMode: stableBulkActionModeSetter,
    selectedApplicationsCount: selectedApplications.size,
    applicationsCount: applications.length,
    showAddColumn,
    setShowAddColumn: stableShowAddColumnSetter,
    handleExportSelected,
    selectedApplications,
    setSelectedApplications: stableSelectedApplicationsSetter,
  }), [
    bulkActionMode,
    selectedApplications.size,
    applications.length,
    showAddColumn,
    handleExportSelected,
    selectedApplications,
    stableBulkActionModeSetter,
    stableShowAddColumnSetter,
    stableSelectedApplicationsSetter,
  ]);

  // Track previous values to detect actual changes
  const prevValuesRef = React.useRef({
    bulkActionMode: false,
    selectedApplicationsCount: 0,
    applicationsCount: 0,
    showAddColumn: false,
  });

  // Only notify parent when primitive values actually change (not on every render)
  React.useEffect(() => {
    if (!onPipelineDataChange) return;

    const prev = prevValuesRef.current;
    const hasChanged =
      prev.bulkActionMode !== bulkActionMode ||
      prev.selectedApplicationsCount !== selectedApplications.size ||
      prev.applicationsCount !== applications.length ||
      prev.showAddColumn !== showAddColumn;

    if (hasChanged) {
      prevValuesRef.current = {
        bulkActionMode,
        selectedApplicationsCount: selectedApplications.size,
        applicationsCount: applications.length,
        showAddColumn,
      };
      onPipelineDataChange(pipelineData);
    }
  }, [onPipelineDataChange, pipelineData, bulkActionMode, selectedApplications.size, applications.length, showAddColumn]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="h-full w-full">
      {/* Header */}
      {!hideHeader && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Pipeline Board</h1>
          <p className="text-sm text-gray-500 mt-1">
            {bulkActionMode 
              ? `${selectedApplications.size} application${selectedApplications.size !== 1 ? 's' : ''} selected`
              : 'Drag and drop applications between stages'
            }
          </p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          {bulkActionMode ? (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setBulkActionMode(false);
                  setSelectedApplications(new Set());
                }}
              >
                <X className="h-4 w-4 mr-1.5" />
                Cancel
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={selectedApplications.size === 0}
                onClick={() => setShowBulkMoveDialog(true)}
              >
                <ArrowRight className="h-4 w-4 mr-1.5" />
                Move {selectedApplications.size > 0 && `(${selectedApplications.size})`}
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={selectedApplications.size === 0}
                onClick={handleExportSelected}
              >
                <Download className="h-4 w-4 mr-1.5" />
                Export
              </Button>
            </>
          ) : (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setBulkActionMode(true)}
              >
                Select Multiple
              </Button>
              <Badge variant="secondary" className="font-medium">
                {applications.length} Total
              </Badge>
              <Dialog open={showAddColumn} onOpenChange={setShowAddColumn}>
                <DialogTrigger asChild>
                  <Button size="sm" className="shadow-sm">
                    <Plus className="h-4 w-4 mr-1.5" />
                    Add Stage
                  </Button>
                </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add New Pipeline Stage</DialogTitle>
                <DialogDescription>
                  Create a custom stage for your application pipeline
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium">Stage Name</label>
                  <Input
                    value={newColumnTitle}
                    onChange={(e) => setNewColumnTitle(e.target.value)}
                    placeholder="e.g., Review, Negotiation"
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Color Theme</label>
                  <div className="flex gap-2 mt-2">
                    {DEFAULT_COLORS.map((color) => (
                      <button
                        key={color}
                        className={`w-8 h-8 rounded-lg border-2 transition-all ${
                          newColumnColor === color 
                            ? 'border-gray-900 scale-110 shadow-sm' 
                            : 'border-transparent hover:border-gray-300'
                        }`}
                        style={{ backgroundColor: color }}
                        onClick={() => setNewColumnColor(color)}
                      />
                    ))}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowAddColumn(false)}>
                  Cancel
                </Button>
                <Button onClick={handleAddColumn}>Create Stage</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
            </>
          )}
        </div>
        </div>
      )}

      {/* Pipeline Board with Drag & Drop */}
      <DndContext
        sensors={sensors}
        collisionDetection={(args) => {
          // If dragging a column, use pointerWithin to avoid detecting applications
          const activeData = args.active.data.current;
          if (activeData?.type === 'column') {
            return pointerWithin(args);
          }
          // For applications, use closestCorners
          return closestCorners(args);
        }}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <div
          id="pipeline-container"
          className={cn(
            "flex gap-3 pb-6 overflow-x-auto",
            hideHeader ? "pt-6 px-4 flex-1 w-full pr-8" : ""
          )}
          style={{ minHeight: 'calc(100vh - 280px)' }}
        >
          <SortableContext
            items={columns.map(col => col.id)}
            strategy={horizontalListSortingStrategy}
          >
            {columns.map((column) => (
              <SortableColumn
                key={column.id}
                column={column}
                columnWidth={columnWidth}
                shouldScroll={shouldScroll}
                isDragging={isDragging}
                dragOverColumn={dragOverColumn}
                bulkActionMode={bulkActionMode}
                selectedApplications={selectedApplications}
                onEditColumn={handleEditColumn}
                onDeleteColumn={handleDeleteColumn}
                onToggleSelection={toggleApplicationSelection}
                onNavigate={(id) => navigate(`/app/merchant-applications/${id}`)}
              />
            ))}
          </SortableContext>
        </div>

        {/* Drag Overlay for visual feedback */}
        <DragOverlay dropAnimation={null}>
          {activeId && activeDragType === 'column' ? (
            <div
              className="rounded-xl shadow-2xl border-2 border-blue-400 opacity-90"
              style={{
                backgroundColor: columns.find(c => c.id === activeId)?.color,
                width: `${columnWidth}px`,
                minWidth: '240px'
              }}
            >
              <div className="p-4 flex items-center gap-3">
                <h3 className="font-bold text-white text-sm">
                  {columns.find(c => c.id === activeId)?.title}
                </h3>
                <div className="bg-white/20 rounded-full px-2 py-1">
                  <span className="text-white font-bold text-xs">
                    {columns.find(c => c.id === activeId)?.applications.length}
                  </span>
                </div>
              </div>
            </div>
          ) : activeId && activeDragType === 'application' && draggedApplication ? (
            <Card
              className="shadow-2xl border-2 border-blue-400 opacity-95 bg-white cursor-grabbing"
              style={{ width: `${columnWidth - 32}px`, minWidth: '200px' }}
            >
              <CardContent className="p-3">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <h4 className="font-medium text-sm truncate text-blue-600">
                      {getPipelineCardTitle(draggedApplication.data)}
                    </h4>
                  </div>
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <div className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      <span>{formatApplicationDate(draggedApplication.createdAt)}</span>
                    </div>
                    <div className="text-xs font-medium text-blue-600 animate-pulse">
                      Drop to move
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : null}
        </DragOverlay>
      </DndContext>


      {/* Edit Column Dialog */}
      <Dialog open={!!editingColumn} onOpenChange={() => setEditingColumn(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Pipeline Column</DialogTitle>
            <DialogDescription>
              Update the column title, color, and email notifications
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6">
            <div>
              <label className="text-sm font-medium">Column Title</label>
              <Input
                value={columnTitle}
                onChange={(e) => setColumnTitle(e.target.value)}
                placeholder="Column title"
                className="mt-1.5"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Color</label>
              <div className="flex gap-2 mt-2">
                {DEFAULT_COLORS.map((color) => (
                  <button
                    key={color}
                    className={`w-8 h-8 rounded-full border-2 ${
                      columnColor === color ? 'border-gray-900' : 'border-transparent'
                    }`}
                    style={{ backgroundColor: color }}
                    onClick={() => setColumnColor(color)}
                  />
                ))}
              </div>
            </div>

            {/* Email Notification Configuration */}
            <div className="border-t pt-4 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-semibold flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Email Notifications
                  </h4>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Send emails when applications move to this stage
                  </p>
                </div>
                <Checkbox
                  checked={columnEmailEnabled}
                  onCheckedChange={(checked) => setColumnEmailEnabled(!!checked)}
                />
              </div>

              {columnEmailEnabled && (
                <>
                  <div>
                    <Label className="text-sm font-medium">Email Template</Label>
                    {isLoadingTemplates ? (
                      <div className="mt-1.5 p-2 border rounded-md bg-gray-50 text-sm text-gray-500">
                        Loading templates...
                      </div>
                    ) : (
                      <Select
                        value={columnEmailTemplateId}
                        onValueChange={setColumnEmailTemplateId}
                      >
                        <SelectTrigger className="mt-1.5">
                          <SelectValue placeholder="Select an email template">
                            {columnEmailTemplateId && merchantEmailTemplates.find((t: any) => t.id === columnEmailTemplateId) ? (
                              <div className="flex items-center gap-2">
                                <span className="font-medium">
                                  {merchantEmailTemplates.find((t: any) => t.id === columnEmailTemplateId)?.type.split('-').map((word: string) =>
                                    word.charAt(0).toUpperCase() + word.slice(1)
                                  ).join(' ')}
                                </span>
                              </div>
                            ) : (
                              'Select an email template'
                            )}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {merchantEmailTemplates.length > 0 ? (
                            merchantEmailTemplates.map((template: any) => (
                              <SelectItem
                                key={template.id || template.type}
                                value={template.id}
                                className="cursor-pointer"
                              >
                                <div className="flex flex-col py-1">
                                  <span className="font-medium text-sm">
                                    {template.type.split('-').map((word: string) =>
                                      word.charAt(0).toUpperCase() + word.slice(1)
                                    ).join(' ')}
                                  </span>
                                  {template.subject && (
                                    <span className="text-xs text-gray-500 mt-0.5">{template.subject}</span>
                                  )}
                                </div>
                              </SelectItem>
                            ))
                          ) : (
                            <SelectItem value="none" disabled>
                              No templates available
                            </SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    )}
                    {!isLoadingTemplates && merchantEmailTemplates.length === 0 && (
                      <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <p className="text-xs text-amber-800 flex items-center gap-2 mb-2">
                          <AlertTriangle className="h-4 w-4" />
                          No email templates configured
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open('/app/tenants/config', '_blank')}
                          className="text-xs h-7"
                        >
                          Create Templates in Tenant Config
                        </Button>
                      </div>
                    )}
                    {templatesError && (
                      <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" />
                        Error loading templates: {templatesError instanceof Error ? templatesError.message : 'Unknown error'}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center space-x-2 bg-gray-50 p-3 rounded-lg">
                    <Checkbox
                      id="auto-send"
                      checked={columnEmailAutoSend}
                      onCheckedChange={(checked) => setColumnEmailAutoSend(!!checked)}
                    />
                    <div className="flex-1">
                      <Label
                        htmlFor="auto-send"
                        className="text-sm font-medium cursor-pointer"
                      >
                        Automatic Email Sending
                      </Label>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {columnEmailAutoSend
                          ? 'Emails will be sent automatically when applications move to this stage'
                          : 'Show preview modal to review and edit email before sending'
                        }
                      </p>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingColumn(null)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateColumn}>Update Column</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Move Dialog */}
      <Dialog open={showBulkMoveDialog} onOpenChange={setShowBulkMoveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Move Selected Applications</DialogTitle>
            <DialogDescription>
              Select the pipeline stage to move {selectedApplications.size} application{selectedApplications.size !== 1 ? 's' : ''} to
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Target Stage</label>
              <Select value={bulkMoveTarget} onValueChange={setBulkMoveTarget}>
                <SelectTrigger className="mt-1.5">
                  <SelectValue placeholder="Select a stage" />
                </SelectTrigger>
                <SelectContent>
                  {columns.map((column) => (
                    <SelectItem key={column.id} value={column.id}>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: column.color }}
                        />
                        <span>{column.title}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkMoveDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleBulkMove} 
              disabled={!bulkMoveTarget || bulkMoveMutation.isPending}
            >
              {bulkMoveMutation.isPending ? 'Moving...' : 'Move Applications'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Column Dialog */}
      <Dialog open={showAddColumn} onOpenChange={setShowAddColumn}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Pipeline Stage</DialogTitle>
            <DialogDescription>
              Create a new stage for your merchant application pipeline
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Stage Title</label>
              <Input
                value={newColumnTitle}
                onChange={(e) => setNewColumnTitle(e.target.value)}
                placeholder="Enter stage title (e.g., 'Documentation Review')"
                className="mt-1.5"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Color</label>
              <div className="flex gap-2 mt-2">
                {DEFAULT_COLORS.map((color) => (
                  <button
                    key={color}
                    className={`w-8 h-8 rounded-full border-2 transition-all ${
                      newColumnColor === color ? 'border-gray-900 ring-2 ring-gray-200' : 'border-transparent hover:border-gray-300'
                    }`}
                    style={{ backgroundColor: color }}
                    onClick={() => setNewColumnColor(color)}
                  />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddColumn(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (newColumnTitle.trim()) {
                  addColumnMutation.mutate({
                    title: newColumnTitle.trim(),
                    color: newColumnColor
                  });
                }
              }}
              disabled={!newColumnTitle.trim() || addColumnMutation.isPending}
            >
              {addColumnMutation.isPending ? 'Adding...' : 'Add Stage'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Preview Modal */}
      <Dialog open={emailPreviewModal.show} onOpenChange={(open) => !open && setEmailPreviewModal({ show: false, applicationId: null, targetStatus: null, template: null })}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Review Email Before Sending
            </DialogTitle>
            <DialogDescription>
              Review and edit the email content before sending to the merchant
            </DialogDescription>
          </DialogHeader>
          {emailPreviewModal.template && (
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium">Subject</Label>
                <Input
                  value={emailPreviewModal.template.subject}
                  onChange={(e) => setEmailPreviewModal(prev => ({
                    ...prev,
                    template: prev.template ? { ...prev.template, subject: e.target.value } : null
                  }))}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Email Content</Label>
                <Textarea
                  value={emailPreviewModal.template.content}
                  onChange={(e) => setEmailPreviewModal(prev => ({
                    ...prev,
                    template: prev.template ? { ...prev.template, content: e.target.value } : null
                  }))}
                  className="mt-1.5 min-h-[300px] font-mono text-sm"
                  placeholder="Email content..."
                />
                <p className="text-xs text-gray-500 mt-2">
                  You can edit the content before sending. Variables like {`{{merchantName}}`} will be replaced automatically.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailPreviewModal({ show: false, applicationId: null, targetStatus: null, template: null })}>
              Cancel
            </Button>
            <Button
              onClick={async () => {
                if (emailPreviewModal.applicationId && emailPreviewModal.targetStatus && emailPreviewModal.template) {
                  try {
                    // Send email via API
                    const response = await fetch('/api/merchant-applications/send-stage-email', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        applicationId: emailPreviewModal.applicationId,
                        stage: emailPreviewModal.targetStatus,
                        subject: emailPreviewModal.template.subject,
                        content: emailPreviewModal.template.content
                      })
                    });

                    if (response.ok) {
                      toast({
                        title: 'Email sent',
                        description: 'Stage notification email has been sent successfully'
                      });
                      setEmailPreviewModal({ show: false, applicationId: null, targetStatus: null, template: null });
                    } else {
                      throw new Error('Failed to send email');
                    }
                  } catch (error) {
                    toast({
                      title: 'Error',
                      description: 'Failed to send email. Please try again.',
                      variant: 'destructive'
                    });
                  }
                }
              }}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Mail className="h-4 w-4 mr-2" />
              Send Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}